/**
 * Pure audio helpers for the voice-intake controller: downsampling, PCM
 * conversion, WAV encoding, and utterance segmentation. No DOM or WebAudio
 * dependencies so everything is unit-testable in jsdom.
 */

import { AdaptiveEnergyGate, type AdaptiveEnergyGateOptions } from "../adaptive_energy_gate"
import { AdaptiveGapThreshold } from "../adaptive_gap_threshold"

export const TARGET_SAMPLE_RATE = 16000

/**
 * Downsample Float32 PCM to 16 kHz by averaging input samples. Audio callbacks
 * rarely align with a complete output sample at 44.1 kHz, so this keeps the
 * unconsumed tail between frames instead of rounding it away on every callback.
 */
export class StreamingDownsampler {
  private remainder = new Float32Array()

  constructor(
    private readonly inputRate: number,
    private readonly outputRate: number = TARGET_SAMPLE_RATE,
  ) {}

  push(input: Float32Array): Float32Array {
    if (this.inputRate <= this.outputRate) return input

    const combined = new Float32Array(this.remainder.length + input.length)
    combined.set(this.remainder)
    combined.set(input, this.remainder.length)

    const ratio = this.inputRate / this.outputRate
    const outputLength = Math.floor(combined.length / ratio)
    const output = new Float32Array(outputLength)
    for (let i = 0; i < outputLength; i++) {
      const start = Math.floor(i * ratio)
      const end = Math.max(start + 1, Math.floor((i + 1) * ratio))
      let sum = 0
      for (let j = start; j < end; j++) sum += combined[j]
      output[i] = sum / (end - start)
    }

    const consumed = Math.floor(outputLength * ratio)
    this.remainder = combined.slice(consumed)
    return output
  }

  reset() {
    this.remainder = new Float32Array()
  }
}

/** Convert Float32 samples (clamped to -1..1) to 16-bit signed PCM. */
export function floatTo16BitPcm(f: Float32Array): Int16Array {
  const output = new Int16Array(f.length)
  for (let i = 0; i < f.length; i++) {
    const sample = Math.max(-1, Math.min(1, f[i]))
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
  }
  return output
}

/** Encode 16-bit PCM as a mono little-endian RIFF/WAVE blob. */
export function encodeWav(pcm: Int16Array, sampleRate: number = TARGET_SAMPLE_RATE): Blob {
  const dataSize = pcm.length * 2
  const buffer = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buffer)

  writeAscii(view, 0, "RIFF")
  view.setUint32(4, 36 + dataSize, true)
  writeAscii(view, 8, "WAVE")
  writeAscii(view, 12, "fmt ")
  view.setUint32(16, 16, true) // fmt subchunk size
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true) // byte rate = rate * channels * 2
  view.setUint16(32, 2, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  writeAscii(view, 36, "data")
  view.setUint32(40, dataSize, true)
  for (let i = 0; i < pcm.length; i++) view.setInt16(44 + i * 2, pcm[i], true)

  return new Blob([buffer], { type: "audio/wav" })
}

function writeAscii(view: DataView, offset: number, text: string) {
  for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i))
}

export interface UtteranceMeta {
  /** Session-clock ms of the first emitted sample (pre-roll included). */
  startMs: number
  /** Session-clock ms of the last voiced frame (the hangover tail is audio, not speech). */
  endMs: number
}

export interface UtteranceSegmenterOptions {
  onUtterance: (samples: Float32Array, meta: UtteranceMeta) => void
  /** Quiet-room energy gate, and the floor the adaptive gate cannot go below. */
  rmsThreshold?: number
  /** Overrides for the noise-floor-relative gate built on rmsThreshold. */
  energyGate?: Omit<AdaptiveEnergyGateOptions, "minThreshold">
  /** Cold-start silence that closes an utterance; adapts from observed pauses. */
  hangoverMs?: number
  minHangoverMs?: number
  maxHangoverMs?: number
  /** Speech resuming this soon after a close means the close split a thought. */
  resumeGraceMs?: number
  /** Shortest first-to-last-voiced span that counts as an utterance. */
  minUtteranceMs?: number
  /**
   * Shortest total of actually-voiced frames inside that span. Room noise
   * spikes bridged by the hangover can span 400ms while voicing 40ms; speech
   * cannot.
   */
  minVoicedMs?: number
  maxUtteranceMs?: number
  preRollMs?: number
}

/**
 * Energy-gate utterance segmenter with an adaptive endpoint. Feed it Float32
 * frames with their start timestamps (ms, monotonic); it emits one
 * concatenated Float32Array per detected utterance via `onUtterance`.
 *
 * While idle, frames accumulate in a rolling pre-roll buffer (preRollMs) so
 * the first syllable isn't clipped. A frame that clears the energy gate opens
 * an utterance (pre-roll included). The utterance closes after the current
 * hangover of continuous silence, is force-cut at maxUtteranceMs (staying
 * open so ongoing speech continues in a fresh buffer), and is discarded when
 * the voiced span is shorter than minUtteranceMs. `flush()` emits any
 * in-progress utterance regardless of length (used on stop so the tail isn't
 * lost).
 *
 * The gate is an AdaptiveEnergyGate: rmsThreshold is its floor, and in a room
 * whose noise sits above that floor it rises to a multiple of the learned
 * noise floor. A fixed gate under the room's noise reads every frame as
 * speech, so nothing ever ends and the force-cut ships max-length blocks of
 * near-silence. For the same reason a force-cut window carrying less than
 * minVoicedMs of voiced audio is dropped rather than emitted, and the
 * segmenter returns to idle: it opened on noise, not on speech.
 *
 * The hangover is an AdaptiveGapThreshold: it starts generous and learns the
 * speakers' own pause profile from the silences speech resumes across, so a
 * halting speaker's within-thought pauses stay inside one utterance. See
 * lib/adaptive_gap_threshold.ts for the model, including how speech resuming
 * shortly after a close feeds back into it.
 */
export class UtteranceSegmenter {
  private readonly onUtterance: (samples: Float32Array, meta: UtteranceMeta) => void
  private readonly gate: AdaptiveEnergyGate
  private readonly hangover: AdaptiveGapThreshold
  private readonly minUtteranceMs: number
  private readonly minVoicedMs: number
  private readonly maxUtteranceMs: number
  private readonly preRollMs: number

  private preRoll: Array<{ frame: Float32Array; timestampMs: number }> = []
  private frames: Float32Array[] = []
  private active = false
  private startMs = 0
  private firstVoicedMs = 0
  private lastVoicedMs = 0
  private lastPushMs: number | null = null
  private voicedMs = 0
  private frameEnergies: number[] = []
  private lastCloseVoicedMs: number | null = null

  constructor(options: UtteranceSegmenterOptions) {
    this.onUtterance = options.onUtterance
    this.gate = new AdaptiveEnergyGate({
      ...options.energyGate,
      minThreshold: options.rmsThreshold ?? 0.012,
    })
    this.hangover = new AdaptiveGapThreshold({
      // Live text is the point of the pause: server-side inference on a chunk
      // takes ~0.6s, so the wait for silence is most of what staff feel. Start
      // tighter than a comfortable reading pause and let the threshold grow
      // for a halting speaker (observeGap/observeResume) rather than making
      // everyone wait for the slowest one.
      initialMs: options.hangoverMs ?? 1200,
      minMs: options.minHangoverMs ?? 800,
      maxMs: options.maxHangoverMs ?? 3500,
      resumeGraceMs: options.resumeGraceMs ?? 1500,
    })
    this.minUtteranceMs = options.minUtteranceMs ?? 400
    // Low enough that a bare "no" or a single digit survives; room-noise
    // spikes are tens of milliseconds each.
    this.minVoicedMs = options.minVoicedMs ?? 150
    this.maxUtteranceMs = options.maxUtteranceMs ?? 15000
    this.preRollMs = options.preRollMs ?? 300
  }

  /** The silence currently required to close an utterance. */
  get hangoverMs(): number {
    return this.hangover.thresholdMs
  }

  /** The energy a frame currently has to reach to count as speech. */
  get energyThreshold(): number {
    return this.gate.threshold
  }

  push(frame: Float32Array, timestampMs: number) {
    const energy = rms(frame)
    const voiced = this.gate.observe(energy)
    // Frame duration is the gap to the previous push; the first frame after
    // a (re)start has no predecessor and counts nothing.
    const frameMs = this.lastPushMs === null ? 0 : timestampMs - this.lastPushMs
    this.lastPushMs = timestampMs

    if (!this.active) {
      if (voiced) this.open(frame, timestampMs)
      else this.bufferPreRoll(frame, timestampMs)
      return
    }

    this.frames.push(frame)
    this.frameEnergies.push(energy)
    if (voiced) {
      this.hangover.observeGap(timestampMs - this.lastVoicedMs)
      this.lastVoicedMs = timestampMs
      this.voicedMs += frameMs
    }

    if (timestampMs - this.firstVoicedMs >= this.maxUtteranceMs) {
      this.closeAtCap(timestampMs, voiced)
      return
    }

    if (!voiced && timestampMs - this.lastVoicedMs >= this.hangover.thresholdMs) {
      if (this.lastVoicedMs - this.firstVoicedMs >= this.minUtteranceMs && this.voicedMs >= this.minVoicedMs) {
        // endMs is the end of speech, not of the hangover: the gaps between
        // utterances (turn grouping) are speech-to-speech.
        this.emit(this.lastVoicedMs)
        this.lastCloseVoicedMs = this.lastVoicedMs
      }
      this.reset()
    }
  }

  private bufferPreRoll(frame: Float32Array, timestampMs: number) {
    this.preRoll.push({ frame, timestampMs })
    while (
      this.preRoll.length > 1 &&
      timestampMs - this.preRoll[0].timestampMs > this.preRollMs
    ) {
      this.preRoll.shift()
    }
  }

  /** Handle a frame that reached the max-utterance cap. */
  private closeAtCap(timestampMs: number, voiced: boolean) {
    // Too little speech in a whole max-length window means the gate opened
    // on room noise: drop the audio and idle rather than send silence to a
    // transcription model, which answers it with hallucinated text.
    if (this.minVoicedMs > 0 && (this.voicedMs < this.minVoicedMs || !this.hasSpeechDynamics())) {
      this.reset()
      return
    }

    // If the cap lands during the adaptive hangover, close at the actual
    // speech boundary. Treating the silent cap frame as voiced erases a
    // real gap and keeps later speech inside the same utterance.
    if (!voiced) {
      this.emit(this.lastVoicedMs)
      this.lastCloseVoicedMs = this.lastVoicedMs
      this.reset()
      return
    }

    this.emit(timestampMs)
    this.frames = []
    this.frameEnergies = []
    this.startMs = timestampMs
    this.firstVoicedMs = timestampMs
    this.lastVoicedMs = timestampMs
    this.voicedMs = 0
  }

  /** Close and emit any in-progress utterance (no minimum-length check). */
  flush() {
    if (this.active && this.frames.length > 0) this.emit(this.lastVoicedMs)
    this.reset()
    // An explicit stop/pause is a real boundary, not a close mid-thought.
    this.lastCloseVoicedMs = null
  }

  private open(frame: Float32Array, timestampMs: number) {
    if (this.lastCloseVoicedMs !== null) {
      this.hangover.observeResume(timestampMs - this.lastCloseVoicedMs)
      this.lastCloseVoicedMs = null
    }

    this.frames = this.preRoll.map((entry) => entry.frame)
    this.frames.push(frame)
    this.frameEnergies = [rms(frame)]
    this.startMs = this.preRoll.length > 0 ? this.preRoll[0].timestampMs : timestampMs
    this.preRoll = []
    this.active = true
    this.firstVoicedMs = timestampMs
    this.lastVoicedMs = timestampMs
    this.voicedMs = 0
  }

  private emit(endMs: number) {
    const total = this.frames.reduce((sum, frame) => sum + frame.length, 0)
    const samples = new Float32Array(total)
    let offset = 0
    for (const frame of this.frames) {
      samples.set(frame, offset)
      offset += frame.length
    }
    this.onUtterance(samples, { startMs: this.startMs, endMs })
  }

  // An amplified headset can hold steady room noise above the scalar energy
  // gate. Real speech changes materially across a max-length window; a steady
  // window does not. Percentiles ignore isolated clicks better than min/max.
  private hasSpeechDynamics(): boolean {
    if (this.frameEnergies.length === 0) return false

    const sorted = [...this.frameEnergies].sort((a, b) => a - b)
    const low = sorted[Math.floor((sorted.length - 1) * 0.1)]
    const high = sorted[Math.floor((sorted.length - 1) * 0.9)]
    return high - low >= 0.015 && high >= low * 1.5
  }

  private reset() {
    this.active = false
    this.frames = []
    this.preRoll = []
    this.startMs = 0
    this.firstVoicedMs = 0
    this.lastVoicedMs = 0
    this.voicedMs = 0
    this.frameEnergies = []
  }
}

function rms(frame: Float32Array): number {
  if (frame.length === 0) return 0
  let sumSquares = 0
  for (let i = 0; i < frame.length; i++) sumSquares += frame[i] * frame[i]
  return Math.sqrt(sumSquares / frame.length)
}
